import type { PlayerOrgResponse } from '@on3/api';
import type { IGenericPageProps } from '@on3/ui-lib/api/schema/custom-contracts';
import withAuthentication from '@on3/ui-lib/hocs/withAuthentication';
import withValidUrl from '@on3/ui-lib/hocs/withValidUrl';
import { proxyApi } from '@on3/ui-lib/utils/api';
import {
  type IPlayerRpmPageProps,
  PlayerRpmPage,
} from '@public/components/Db/Profile/Rpm/PlayerRpmPage';
import { PageWrapper } from '@public/components/Layouts/PageWrapper';
import { type GetServerSideProps } from 'next';

interface IRpmPage extends IPlayerRpmPageProps, IGenericPageProps {}

const RpmPage = ({
  rpmSummary,
  basicInfo,
  rpmPicks,
  playerOrganizations,
  ...props
}: IRpmPage) => {
  const noIndex = rpmSummary?.predictions?.length === 0;
  const description = `${basicInfo?.name} Recruiting Prediction Machine `;
  const title = `${basicInfo?.name} RPM`;

  return (
    <PageWrapper
      {...props}
      description={description}
      noIndex={noIndex}
      title={title}
    >
      <PlayerRpmPage
        basicInfo={basicInfo}
        playerOrganizations={playerOrganizations}
        rpmPicks={rpmPicks}
        rpmSummary={rpmSummary}
      />
    </PageWrapper>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const siteData = await withValidUrl.isValid(ctx);
  const userData = await withAuthentication.isAuth(ctx);

  if (!siteData) {
    return {
      notFound: true,
    };
  }

  const { id, recruitingId } = ctx.query;
  const playerId = id?.toString().split('-').pop();

  if (!playerId) {
    return {
      notFound: true,
    };
  }

  try {
    const [basicInfo] = await Promise.allSettled([
      proxyApi
        .get(`/public/rdb/v1/player/${playerId}/profile`)
        .then((r) => r.data),
    ]);

    if (basicInfo.status !== 'fulfilled') {
      return {
        notFound: true,
      };
    }

    const fetchedPlayer = basicInfo.value;

    const [rpmSummary, rpmPicks, playerOrganizations] =
      await Promise.allSettled([
        proxyApi
          .get(`/public/rdb/v1/recruitments/${recruitingId}/rpm-summary`)
          .then((r) => r.data),
        proxyApi
          .get(`/public/rdb/v1/recruitments/${recruitingId}/rpm-picks`)
          .then((r) => r.data),
        proxyApi
          .get<PlayerOrgResponse>(
            `/public/rdb/v1/player/${fetchedPlayer.personSportKey}/organizations`,
          )
          .then((r) => r.data),
      ]);

    const data = {
      pageType: 'database',
      siteData: siteData || [],
      userData: userData || {},
      basicInfo: basicInfo.status === 'fulfilled' ? basicInfo.value : [],
      rpmSummary: rpmSummary.status === 'fulfilled' ? rpmSummary.value : [],
      rpmPicks: rpmPicks.status === 'fulfilled' ? rpmPicks.value : [],
      playerOrganizations:
        playerOrganizations.status === 'fulfilled'
          ? playerOrganizations.value
          : {},
    };

    return { props: { ...data } };
  } catch (e) {
    return { props: [] };
  }
};

export default withValidUrl(RpmPage);
